// =============================================================================
// Order Stats Bar — compact single-line operational overview
// =============================================================================
// Replaces the old 6-card stats grid. Shows only the operationally important
// counts in one horizontal row; each item doubles as a quick filter — clicking
// it writes the corresponding filter into the URL (handled by the parent).
//
// Metric definitions (computed in OrderService.getOrderStats):
//   Today            createdAt >= server-UTC start of day
//   Pending          active pipeline (not terminal, not shipped)   [display-only]
//   Payment pending  wholesaleChargeStatus IN (PENDING, CHARGING, ON_HOLD)
//   Unrouted         no resolved provider on a non-terminal order
//   Total            all orders
// =============================================================================

import { Card } from '@tkpod/ui';

interface OrderStatsBarProps {
  stats: Record<string, number> | undefined;
  isLoading: boolean;
  /** Applies the quick filter associated with a stat item. */
  onSelect: (key: StatKey) => void;
}

export type StatKey = 'TODAY' | 'PENDING_ACTIVE' | 'PAYMENT_PENDING' | 'UNROUTED' | 'TOTAL';

const STAT_ITEMS: Array<{
  key: StatKey;
  label: string;
  dotColor: string;
  clickable: boolean;
  title: string;
}> = [
  { key: 'TODAY', label: 'Today', dotColor: 'bg-blue-500', clickable: true, title: 'Orders created today — click to filter' },
  { key: 'PENDING_ACTIVE', label: 'Pending', dotColor: 'bg-amber-500', clickable: false, title: 'Active orders not yet shipped' },
  { key: 'PAYMENT_PENDING', label: 'Payment pending', dotColor: 'bg-red-500', clickable: true, title: 'Wholesale charge awaiting collection — click to filter' },
  { key: 'UNROUTED', label: 'Unrouted', dotColor: 'bg-purple-500', clickable: true, title: 'No provider resolved — click to filter' },
  { key: 'TOTAL', label: 'Total', dotColor: 'bg-gray-400', clickable: true, title: 'All orders — click to clear filters' },
];

export function OrderStatsBar({ stats, isLoading, onSelect }: OrderStatsBarProps) {
  return (
    <Card padding="none">
      <div className="flex items-center gap-1 overflow-x-auto px-3 py-2">
        {STAT_ITEMS.map((item, i) => {
          const value = stats?.[item.key] ?? 0;
          const content = (
            <>
              <span className={`h-2 w-2 shrink-0 rounded-full ${item.dotColor}`} aria-hidden />
              <span className="text-xs font-medium text-text-secondary">{item.label}</span>
              <span className="text-sm font-bold tabular-nums text-text-primary">
                {isLoading ? '—' : value.toLocaleString()}
              </span>
            </>
          );
          return (
            <div key={item.key} className="flex shrink-0 items-center">
              {i > 0 && <span className="mx-2 h-4 w-px bg-border" aria-hidden />}
              {item.clickable ? (
                <button
                  type="button"
                  onClick={() => onSelect(item.key)}
                  title={item.title}
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors hover:bg-surface-secondary"
                >
                  {content}
                </button>
              ) : (
                <span title={item.title} className="inline-flex items-center gap-1.5 px-2 py-1">
                  {content}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
